import { Injectable, signal, computed, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { UnleashClient } from 'unleash-proxy-client';
import { environment } from '../../../environments/environment';

export interface FeatureFlags {
    members: boolean;
    giving: boolean;
    events: boolean;
    ministries: boolean;
    checkIn: boolean;
    sermons: boolean;
    campaigns: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class FeatureFlagService {
    private readonly platformId = inject(PLATFORM_ID);
    private readonly unleash: UnleashClient | null = null;

    // Default flags for Release 1 (Fallback)
    private readonly _flags = signal<FeatureFlags>({
        members: true,
        giving: true,
        events: true,
        ministries: true,
        checkIn: false,
        sermons: false,
        campaigns: true
    });

    readonly flags = this._flags.asReadonly();

    readonly isMembersEnabled = computed(() => this._flags().members);
    readonly isGivingEnabled = computed(() => this._flags().giving);
    readonly isEventsEnabled = computed(() => this._flags().events);
    readonly isMinistriesEnabled = computed(() => this._flags().ministries);
    readonly isCheckInEnabled = computed(() => this._flags().checkIn);
    readonly isSermonsEnabled = computed(() => this._flags().sermons);
    readonly isCampaignsEnabled = computed(() => this._flags().campaigns);

    constructor() {
        if (isPlatformBrowser(this.platformId)) {
            this.unleash = new UnleashClient({
                url: environment.unleashUrl,
                clientKey: environment.unleashClientKey,
                appName: 'church-cms-web',
                refreshInterval: 15, // 15 seconds
            });

            this.unleash.on('update', () => {
                this.updateFlags();
            });

            this.unleash.on('ready', () => {
                this.updateFlags();
            });

            // Start polling
            this.unleash.start();
        }
    }

    private updateFlags() {
        if (!this.unleash) return;

        this._flags.update(current => ({
            ...current,
            members: this.unleash!.isEnabled('members'),
            giving: this.unleash!.isEnabled('giving'),
            events: this.unleash!.isEnabled('events'),
            ministries: this.unleash!.isEnabled('ministries'),
            checkIn: this.unleash!.isEnabled('check_in'),
            sermons: this.unleash!.isEnabled('sermons'),
            campaigns: this.unleash!.isEnabled('campaigns'),
        }));
    }
}
